import { Box, Button, Loader, StaticTable, Status, StatusBadge } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./integration-sync-logs.scss";
import { State, workflowService, WorkflowStateDetails } from "services/workflows/WorkflowService";
import { formatTimestamp } from "utils/format";

interface IntegrationSyncLogsProps {
    workflowUuid: string;
}

const formatDate = (dateString: string) => formatTimestamp(dateString, false, true);

const toSyncStatus = (state: State): Status => {
    if (state === State.SUCCEEDED) {
        return Status.SUCCESS;
    }
    if (state === State.FAILED) {
        return Status.ERROR;
    }
    return Status.INFO;
};

export function IntegrationSyncLogs(props: IntegrationSyncLogsProps) {
    const { t } = useTranslation();

    const [logs, setLogs] = React.useState<WorkflowStateDetails[]>([]);
    const [cursor, setCursor] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [requestFailed, setRequestFailed] = React.useState(false);

    const fetchLogs = (initialLoad: boolean, abortController: AbortController) => {
        if (initialLoad) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        workflowService
            .fetchWorkflowStates(
                props.workflowUuid,
                { state: State.ALL, test_mode: false, from: undefined, to: undefined, report_uuid: "" },
                initialLoad ? null : cursor,
                abortController
            )
            .then((data) => {
                setLogs((prev) => (initialLoad ? data.workflowStateDetails : [...prev, ...data.workflowStateDetails]));
                setCursor(data.cursor);
                setRequestFailed(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailed(true);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setLoadingMore(false);
                }
            });
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        fetchLogs(true, abortController);
        return () => abortController.abort();
    }, [props.workflowUuid]);

    const getSyncStatusLabel = (state: State): string => {
        if (state === State.SUCCEEDED) {
            return t("Integrations.details.syncLogs.statusActive");
        }
        if (state === State.FAILED) {
            return t("Integrations.details.syncLogs.statusSyncFailed");
        }
        return t("Integrations.details.syncLogs.statusInProgress");
    };

    if (requestFailed) {
        return <div className={style.message}>{t("Integrations.details.syncLogs.requestFailed")}</div>;
    }

    const headers = [
        { value: t("Integrations.details.syncLogs.columnSyncDuration") },
        { value: t("Integrations.details.syncLogs.columnSyncStatus") },
    ];

    const logRows = logs.map((log) => [
        <td key={`${log.contextId}-duration`}>
            {t("Integrations.details.syncLogs.syncDuration", {
                from: formatDate(log.started),
                to: formatDate(log.completed),
            })}
        </td>,
        <td key={`${log.contextId}-status`}>
            <Box verticalAlignment="Center" gap="Xs">
                <StatusBadge value={getSyncStatusLabel(log.state)} status={toSyncStatus(log.state)} />
            </Box>
        </td>,
    ]);

    const styledCells = cursor
        ? [
              ...logRows,
              [
                  <td key="load-more" colSpan={2} className={style.loadMoreCell}>
                      <Box verticalAlignment="Center" horizontalAlignment="Center" gap="Xs">
                          {loadingMore ? (
                              <Loader small />
                          ) : (
                              <Button small variant="Primary" onClick={() => fetchLogs(false, new AbortController())}>
                                  {t("Common.loadMore")}
                              </Button>
                          )}
                      </Box>
                  </td>,
              ],
          ]
        : logRows;

    return (
        <Box columnLayout>
            <div className={style.tableScrollArea}>
                <StaticTable
                    headers={headers}
                    styledCells={styledCells}
                    loading={loading}
                    emptyStateMessage={t("Integrations.details.syncLogs.emptyState")}
                    tableClass={style.table}
                />
            </div>
        </Box>
    );
}
